/**
 * NGO Manager Impact Dashboard — /ngo/manage/:slug
 *
 * Auth-gated. The backend enforces access: admin OR the user's email is in
 * `manager_emails` for this partner. Shows aggregate signup + retention +
 * upgrade metrics — no PII (no member emails).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  Users,
  TrendingUp,
  Award,
  Calendar,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Metric = ({ label, value, icon: Icon, testid }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-black text-slate-900" data-testid={testid}>
            {value?.toLocaleString('en-US') ?? 0}
          </p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function NgoManageDashboard() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      setForbidden(true);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ngos/${slug}/impact`);
        if (!cancel) setData(res.data);
      } catch (err) {
        if (!cancel) {
          if (err?.response?.status === 403) {
            setForbidden(true);
          } else if (err?.response?.status === 404) {
            toast.error('Partner not found');
          } else {
            toast.error(err?.response?.data?.detail || 'Failed to load dashboard');
          }
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug, user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" data-testid="ngo-manage-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center" data-testid="ngo-manage-forbidden">
        <h1 className="text-3xl font-bold mb-3">Not authorized</h1>
        <p className="text-muted-foreground mb-6">
          You need to be listed as a manager on this partner to view impact stats. Ask an admin to
          add your email in <code>/admin/ngos</code>.
        </p>
        <Button asChild>
          <Link to={user ? '/dashboard' : '/'}>Go back</Link>
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="ngo-manage-page">
      <SEO title={`Impact — ${data.name}`} description="Signup and retention impact metrics." />
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {data.logo_url ? (
            <img src={data.logo_url} alt={data.name} className="h-12 w-12 rounded object-cover" />
          ) : (
            <div className="h-12 w-12 rounded bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
              {data.name?.[0] || '?'}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{data.name}</h1>
            <p className="text-xs text-muted-foreground">Impact dashboard · slug <code>{data.slug}</code></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to={`/partners/${data.slug}`}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Public page
            </Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Metric
          label="Total signups"
          value={data.total_signups}
          icon={Users}
          testid="ngo-metric-total"
        />
        <Metric
          label="Signups (30 days)"
          value={data.signups_last_30_days}
          icon={TrendingUp}
          testid="ngo-metric-30"
        />
        <Metric
          label="Signups (7 days)"
          value={data.signups_last_7_days}
          icon={Calendar}
          testid="ngo-metric-7"
        />
        <Metric
          label="Upgraded to Charter"
          value={data.upgraded_to_charter}
          icon={Award}
          testid="ngo-metric-upgraded"
        />
      </div>

      <Card className="mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Currently active free-year members
            </p>
          </div>
          <p className="text-3xl font-black text-slate-900" data-testid="ngo-metric-active">
            {data.active_free_year_members?.toLocaleString('en-US') || 0}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Members you referred who still have their free membership active.
          </p>
        </CardContent>
      </Card>

      {/* Recent signups */}
      <Card>
        <CardContent className="p-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Recent signups (last 20)
          </p>
          {(!data.recent_signups || data.recent_signups.length === 0) ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center">
              No signups yet. Hand out those NGO cards!
            </p>
          ) : (
            <div className="divide-y">
              {data.recent_signups.map((r) => (
                <div
                  key={r.id}
                  className="py-2 flex items-center justify-between text-sm"
                  data-testid="ngo-recent-signup-row"
                >
                  <div className="flex items-center gap-2 text-slate-700">
                    <Award className="h-3.5 w-3.5 text-amber-500" />
                    <span>New free-year member</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-3">
            Privacy: we don't share member emails with partners. Aggregate counts only.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
